/**
 * Where the weekly email's "turn it off" link lands. No sign-in: the link is signed for
 * one person. It asks before acting, because mail scanners follow links — a page that
 * unsubscribed on load would unsubscribe whoever's scanner got there first.
 */

import { useMutation } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";

import { Button } from "../../components/ui/button";
import { ApiError, api } from "../../lib/api";
import { AuthShell } from "./AuthPages";

export function UnsubscribePage() {
  const [params] = useSearchParams();
  const userId = params.get("u") ?? "";
  const token = params.get("t") ?? "";
  const malformed = !/^\d+$/.test(userId) || token.length < 8;

  const stop = useMutation({
    mutationFn: () =>
      api.post(
        `/digest/unsubscribe?u=${encodeURIComponent(userId)}&t=${encodeURIComponent(token)}`,
      ),
  });

  if (malformed) {
    return (
      <AuthShell title="This link is incomplete">
        <p className="text-sm text-muted">
          You can turn the weekly email off in{" "}
          <Link to="/settings" className="text-accent underline underline-offset-2">
            Settings
          </Link>
          .
        </p>
      </AuthShell>
    );
  }

  if (stop.isSuccess) {
    return (
      <AuthShell title="Done — no more weekly emails">
        <p className="text-sm text-muted">
          You can turn it back on any time in{" "}
          <Link to="/settings" className="text-accent underline underline-offset-2">
            Settings
          </Link>
          .
        </p>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Stop the weekly summary?">
      <p className="mb-5 text-sm">
        You will stop getting the Sunday email about entries other people added that include
        you. Nothing else changes.
      </p>
      {stop.isError ? (
        <p role="alert" className="mb-3 text-sm text-owing">
          {stop.error instanceof ApiError ? stop.error.message : "That did not work. Try again."}
        </p>
      ) : null}
      <Button variant="primary" disabled={stop.isPending} onClick={() => stop.mutate()}>
        {stop.isPending ? "Stopping…" : "Stop the weekly email"}
      </Button>
    </AuthShell>
  );
}
